

import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isValidObjectId } from "@/lib/utils/input-validator";
import {
  GUEST_REFETCH_FIELDS,
  getCompanions,
  updateGuests,
} from "@/lib/api/handlers/GuestHandling";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";
import { Guest } from "../types/Guest";
import type { DeleteGuestsResult } from "@/types/GuestTrash";
import { GuestType } from "../types/enums/GuestType";
import type { GuestsCreatedDetail } from "./GuestsProvider";
import { AcceptanceState } from "../types/enums/AcceptanceState";
import type { GuestAcceptanceResolvedDetail } from "@/components/PGuestDetails/guest-actions/components/AcceptanceActions";
import { armGuestReconcile } from "@/lib/guest-reconcile/reconcileScheduler";
import { dropGuestFromRequestsCache } from "@/lib/query/requestsCache";
import { COMPANION_SCOPE, type CompanionScope } from "@/types/enums/CompanionScope";
import { useGuestsOptional } from "./GuestsProvider";

type GuestCompanionsMap = Map<string, Guest[] | null>;

type GuestCompanionsContextType = {
  /** The event this panel belongs to. Exposed because a row-level action needs
   *  it (the delete-impact read) and reaching for the route param would break
   *  on every surface that opens the popup from outside an event route. */
  eventId: string;
  allCompanions: GuestCompanionsMap;
  /** Host guestIds whose companion list could not be loaded. */
  loadErrors: Map<string, string>;
  /**
   * A DECISION read: this host's companions in `scope`, answered to the caller
   * and to nobody else.
   *
   * Deliberately separate from `fetchCompanions`, which exists to fill the
   * panel: this one writes no shared list, raises no panel spinner and no
   * panel error banner, and — the point — reports its own failure in its own
   * return value.
   *
   * Both of those matter, and both were learned the hard way in this file:
   *  • a decision read that WROTE the shared list put declined and archived
   *    rows into a panel whose dropdown still said "party", from a toolbar
   *    click that had nothing to do with the list;
   *  • a failure reported through React state cannot be seen by the closure
   *    that awaited the call (state is replaced immutably, the closure holds
   *    the old copy), so "could not read" silently became "no companions" —
   *    the archive cascade then ran with no confirmation.
   */
  peekCompanions: (
    guestId: string,
    scope: CompanionScope,
  ) => Promise<{ rows: Guest[] } | { failed: true }>;
  loading: boolean;
  setAllCompanionsState: React.Dispatch<
    React.SetStateAction<GuestCompanionsMap>
  >;
  errorMessage: string | null;
  /**
   * Load a host's companions in the named scope and RETURN them.
   *
   * The return value is what lets a caller that needs a decision — the
   * archive/unarchive confirmation, the "+N placeholders" numbering — name the
   * set it means instead of reading whatever the last opened tab happened to
   * leave in the shared map. Callers that only render keep ignoring it.
   */
  fetchCompanions: (
    guestId: string,
    refetch?: boolean,
    scope?: CompanionScope,
  ) => Promise<Guest[]>;
  acceptCompanion: (companionId: string, mainGuestId?: string) => Promise<void>;
  declineCompanion: (companionId: string, mainGuestId?: string) => Promise<void>;
  /**
   * Remove a companion for good — the guest-delete lane, not a state flip.
   *
   * The popup's trash used to call `declineCompanion`, so a "deleted"
   * companion survived as a DECLINED row: gone from the guest list, back in
   * this panel on the next read, and still printed in the mail's companion
   * table. The confirmation dialog has always promised the opposite
   * ("kann nicht rückgängig gemacht werden").
   */
  /** W6: answers the delete door's result (TRASH batch to undo by), null on an old backend. */
  deleteCompanion: (companionId: string, mainGuestId?: string, options?: { permanent?: boolean }) => Promise<DeleteGuestsResult | null>;
  /** Put a declined companion back into the party (the approve write). */
  restoreCompanion: (companionId: string, mainGuestId?: string) => Promise<void>;
};

const GuestCompanionsContext = createContext<
  GuestCompanionsContextType | undefined
>(undefined);


/** Current `state` of a companion wherever it sits in the map — the sidebar is
 *  keyed by MAIN guest, so the companion has to be found by scanning. */
function findCompanionState(
  map: GuestCompanionsMap,
  companionId: string,
): unknown {
  for (const list of map.values()) {
    const hit = list?.find((c) => c.guestId === companionId);
    if (hit) return hit.state;
  }
  return undefined;
}

export const GuestCompanionsProvider = ({
  children,
  eventId,
}: {
  children: React.ReactNode;
  eventId: string;
}) => {
  // Initialize with a Map instead of an array
  const [allCompanionsState, setAllCompanionsState] =
    useState<GuestCompanionsMap>(new Map());
  /** Keyed per host guestId, so one failure cannot mask another guest's list. */
  const [loadErrors, setLoadErrors] = useState<Map<string, string>>(new Map());

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // OPTIONAL on purpose. Every surface that opens the guest popup mounts a
  // `GuestsProvider` above this one (`StandaloneGuestDetail`, the guest-list
  // page, the requests page), but this provider is also mounted bare in tests
  // and could be again in a future surface — and a provider that throws on
  // mount would take that whole surface down for a feature it is not using.
  // `deleteCompanion` refuses loudly instead, at the moment it is called.
  const guests = useGuestsOptional();
  /**
   * Which scope each host's cached list was loaded with.
   *
   * The public map is keyed by host id alone, and two surfaces of the same
   * popup want different sets: the companions panel shows the party (plus, by
   * default, the open requests), while the Doku timeline needs every row that
   * ever existed. Without this, whichever tab opened first decided what the
   * other one rendered — and "declined companions appear in the panel" is
   * exactly the bug this whole change is about.
   */
  const loadedScopeRef = useRef<Map<string, CompanionScope>>(new Map());

  // A sidebar accept/decline resolves a companion REQUEST — the exact same
  // write AcceptanceActions performs, so mirror its bookkeeping: broadcast
  // guestAcceptanceResolved (the requests page's CompanionsProvider drops the
  // row + fixes total/badge + shields the id) and stale the outliving query
  // snapshots so a sub-staleTime remount can't resurrect the actioned row.
  const broadcastResolved = useCallback(
    (companionId: string, state: AcceptanceState) => {
      window.dispatchEvent(
        new CustomEvent<GuestAcceptanceResolvedDetail>(
          "guestAcceptanceResolved",
          { detail: { eventId, guestId: companionId, state, kind: "companions" } },
        ),
      );
      // The event only reaches a MOUNTED requests provider, and a tab switch
      // unmounts one of the two. Dropping the row from the cached snapshot is
      // what actually keeps the resolved request from coming back.
      // The kind is known here — this path only ever resolves COMPANIONS — and
      // passing it is what makes the count drop for a row that is not on the
      // cached first page (the admin was on page 2+). Passing the WRONG kind
      // would decrement a count the guest never belonged to, so this argument
      // is pinned by test, not just by comment.
      dropGuestFromRequestsCache(queryClient, eventId, companionId, "companions");
      void queryClient.invalidateQueries({
        queryKey: ["guestsRequests", eventId],
        refetchType: "none",
      });
      void queryClient.invalidateQueries({
        queryKey: ["guestlist", eventId],
        refetchType: "none",
      });
      void queryClient.invalidateQueries({
        queryKey: ["guestDetailGuest", eventId, companionId],
        refetchType: "none",
      });
    },
    [eventId, queryClient],
  );

  const peekCompanions = useCallback(
    async (
      guestId: string,
      scope: CompanionScope,
    ): Promise<{ rows: Guest[] } | { failed: true }> => {
      if (!isValidObjectId(eventId) || !isValidObjectId(guestId)) {
        return { failed: true };
      }
      try {
        const rows = await getCompanions(
          eventId,
          guestId,
          `${GUEST_REFETCH_FIELDS},companions`,
          scope,
        );
        if (!Array.isArray(rows)) return { failed: true };
        return { rows };
      } catch {
        return { failed: true };
      }
    },
    [eventId],
  );

  const fetchCompanions = useCallback(
    async (guestId: string, refetch?: boolean, scope?: CompanionScope) => {
    setErrorMessage(null);

    const wantedScope = scope ?? COMPANION_SCOPE.PARTY_AND_REQUESTS;

    // A cached list only answers the question it was fetched for. The scope
    // check is what makes that true; `refetch` stays because a caller that
    // KNOWS the server moved (a switch, a delete) must be able to say so
    // without inventing a scope change.
    if (
      allCompanionsState.has(guestId) &&
      !refetch &&
      loadedScopeRef.current.get(guestId) === wantedScope
    ) {
      setLoading(false);
      return allCompanionsState.get(guestId) ?? [];
    }
    setLoading(true);

    try {
      if (!isValidObjectId(eventId)) throw new Error("validationError");
      if (!isValidObjectId(guestId)) throw new Error("validationError");

      // Shared projection, not a hand-written copy: a companion opens the SAME
      // detail popup, so it needs the same fields — notably `origin`/`importId`
      // for its display-only cells, which the old literal here lacked.
      //
      // (This path mounts GuestDetailBody DIRECTLY, so unlike the TCCCompanions
      // one it has no refetch to keep alive — the projection is its only source.
      // Two hand-written near-copies had already drifted apart; one constant
      // means the next cell added cannot be empty here alone.)
      let companions = await getCompanions(
        eventId,
        guestId,
        `${GUEST_REFETCH_FIELDS},companions`,
        // NAMED, never left to the route's default.
        //
        // The route's default is the set it has always answered — anything but
        // DECLINED, archived rows included — and that is precisely what made a
        // "deleted" companion come back: the trash writes DECLINED, the guest
        // list stopped showing the row, and this panel handed it straight back
        // on the next read. The panel now says which party it means, and
        // `PARTY` is the guest list's own predicate rather than a lookalike.
        wantedScope,
      );

      if ("isServerError" in companions && companions.isServerError === true)
        throwServerError(companions);

      companions = companions as Guest[];

      // Create a new Map with the updated data to ensure state change detection
      setLoadErrors((prev) => {
        if (!prev.has(guestId)) return prev;
        const next = new Map(prev);
        next.delete(guestId);
        return next;
      });
      loadedScopeRef.current.set(guestId, wantedScope);
      const loaded = companions as Guest[];
      setAllCompanionsState((prev) => {
        const newMap = new Map(prev);
        newMap.set(guestId, loaded);
        return newMap;
      });
      return loaded;
    } catch (error) {
      let message = "unknown";
      if (error instanceof Error) message = error.message;
      setErrorMessage(message);
      // Deliberately NOT caching `[]` here any more.
      //
      // The early return at the top of this callback keys on
      // `allCompanionsState.has(guestId)`, so an `[]` written on failure was
      // PERMANENT for the session: one blip and the Companions tab showed
      // "no companions" — an empty list, not an error — for the rest of the
      // visit, with no retry. Since the merged guest timeline started calling
      // this from the Doku tab, the Doku tab became the first caller and could
      // poison a tab the user had not even opened.
      //
      // Leaving the key ABSENT keeps the failure retryable and lets consumers
      // tell "not loaded" from "loaded, none".
      // The key stays ABSENT from the map on failure (see above), so drop the
      // scope note with it — otherwise the next call would compare against a
      // scope whose list was never stored.
      loadedScopeRef.current.delete(guestId);
      setLoadErrors((prev) => new Map(prev).set(guestId, message));
      // An empty list, never a throw: every caller of this is a render path or
      // a best-effort decision, and the error is already recorded per host in
      // `loadErrors` for the surface that wants to say so.
      return [];
    } finally {
      setLoading(false);
    }
  },
    [eventId, allCompanionsState],
  );

  /**
   * Companions created ANYWHERE in this event land in the host's list here.
   *
   * This list is fetched once per host and then held for the page's lifetime
   * (see the early return in `fetchCompanions`), so anything that creates a
   * companion without telling it is invisible until the operator leaves the
   * page. Two lanes did exactly that: the capacity popup's "add anyway" /
   * "waitlist" confirmations, which create from a dialog that has never heard
   * of this provider — and the guest popup's placeholder quick-add reads this
   * very list to number its rows, so a create it did not see made the NEXT one
   * restart at "TBD 1" and mint duplicates.
   *
   * Deduped by guestId: the event is a broadcast and a caller that appends the
   * rows itself must not produce them twice. A host whose list was never
   * loaded is left alone — seeding the key here would make the fetch-once
   * effect skip the load and show the new rows as if they were the whole party.
   */
  useEffect(() => {
    const onGuestsCreated = (event: Event) => {
      const detail = (event as CustomEvent<GuestsCreatedDetail>).detail;
      if (!detail || detail.eventId !== eventId) return;
      const byHost = new Map<string, Guest[]>();
      for (const guest of detail.guests) {
        if (guest.guestType !== GuestType.COMPANION) continue;
        if (!guest.mainGuest || !guest.guestId) continue;
        byHost.set(guest.mainGuest, [...(byHost.get(guest.mainGuest) ?? []), guest]);
      }
      if (byHost.size === 0) return;
      setAllCompanionsState((prev) => {
        let next: GuestCompanionsMap | null = null;
        for (const [hostId, rows] of byHost) {
          const current = prev.get(hostId);
          if (!current) continue;
          const known = new Set(current.map((c) => c.guestId));
          const fresh = rows.filter((row) => !known.has(row.guestId));
          if (fresh.length === 0) continue;
          next = next ?? new Map(prev);
          next.set(hostId, [...current, ...fresh]);
        }
        return next ?? prev;
      });
    };
    window.addEventListener("guestsCreated", onGuestsCreated);
    return () => window.removeEventListener("guestsCreated", onGuestsCreated);
  }, [eventId]);

  const acceptCompanion = useCallback(async (
    companionId: string,
    mainGuestId?: string,
    /**
     * Does this write RESOLVE a pending request?
     *
     * True for the ✓ on an open request — the requests page then drops the row
     * and every counter takes one off. False for a restore: a declined
     * companion never sat in the pending set, so announcing it would move a
     * number that has nothing to do with it (the off-page branch of
     * `dropGuestFromRequestsCache` and the cross-tab badge listener both
     * decrement unconditionally).
     */
    opts?: { announceResolvedRequest?: boolean },
  ) => {
    const response = await updateGuests(eventId, [
      {
        guestId: companionId,
        acceptanceState: AcceptanceState.ACCEPTED,
      },
    ]);

    if (
      typeof response === "object" &&
      // `typeof null === "object"`, so without this the `in` below throws a
      // TypeError in the middle of an accept/decline handler the moment this
      // endpoint answers with a JSON null. It held only because the response
      // was `undefined`, which short-circuits on `typeof` — an accident, not a
      // guard.
      response !== null &&
      "isServerError" in response &&
      response.isServerError === true
    ) {
      throwServerError(response);
    }

    if (opts?.announceResolvedRequest !== false) {
      broadcastResolved(companionId, AcceptanceState.ACCEPTED);
    }
    // The approval fires COMPANION_* triggers whose UPDATE_GUEST runs
    // after this response — watch the companion so the sidebar shows the
    // automation's result rather than ours.
    armGuestReconcile({
      eventId,
      watches: new Map([
        [
          companionId,
          [
            // `state` is untouched by this write, so the value currently on
            // screen is a valid baseline — and it is the field a
            // "companion approved → set Status" rule actually writes.
            {
              field: "state",
              isAdditional: false,
              fromRaw: findCompanionState(allCompanionsState, companionId),
            },
            // acceptanceState we just wrote ourselves, so the baseline has to
            // be the value we wrote or the first poll succeeds on our change.
            { field: "acceptanceState", isAdditional: false, fromRaw: AcceptanceState.ACCEPTED },
          ],
        ],
      ]),
    });

    // Flip the entry IN PLACE (accepted companions stay listed in the sidebar
    // with the trash icon) under the MAIN-guest key — the map is keyed by the
    // guest whose companions were fetched, never by the companion itself.
    // Fall back to scanning every list when the caller couldn't supply it.
    setAllCompanionsState((prev) => {
      const flip = (list: Guest[]) =>
        list.map((c) =>
          c.guestId === companionId
            ? { ...c, acceptanceState: AcceptanceState.ACCEPTED }
            : c,
        );
      const next = new Map(prev);
      const keyed = mainGuestId ? next.get(mainGuestId) : undefined;
      if (keyed) {
        next.set(mainGuestId!, flip(keyed));
        return next;
      }
      let touched = false;
      next.forEach((list, key) => {
        if (list?.some((c) => c.guestId === companionId)) {
          next.set(key, flip(list));
          touched = true;
        }
      });
      return touched ? next : prev;
    });
  }, [eventId, broadcastResolved]);

  const declineCompanion = useCallback(async (companionId: string, mainGuestId?: string) => {
    // No provider-wide `loading`/`errorMessage` here either: both make
    // `CompanionsList` replace its rows, which tears down the confirmation
    // dialog this write was started from. See `deleteCompanion`.
    //
    // And no try/catch: a refused decline REJECTS, and the row renders the
    // reason in that dialog. It used to be swallowed here, which made a
    // refusal look exactly like a successful rejection.
    const response = await updateGuests(eventId, [
      {
        guestId: companionId,
        acceptanceState: AcceptanceState.DECLINED,
      },
    ]);

    if (
      typeof response === "object" &&
        // Same null guard as the sibling above — `in null` throws.
      response !== null &&
      "isServerError" in response &&
      response.isServerError === true
    ) {
      throwServerError(response);
    }

    broadcastResolved(companionId, AcceptanceState.DECLINED);
    // The approval fires COMPANION_* triggers whose UPDATE_GUEST runs
    // after this response — watch the companion so the sidebar shows the
    // automation's result rather than ours.
    armGuestReconcile({
    eventId,
    watches: new Map([
      [
        companionId,
        [
            // `state` is untouched by this write, so the value currently on
            // screen is a valid baseline — and it is the field a
            // "companion approved → set Status" rule actually writes.
          {
            field: "state",
            isAdditional: false,
            fromRaw: findCompanionState(allCompanionsState, companionId),
          },
            // acceptanceState we just wrote ourselves, so the baseline has to
            // be the value we wrote or the first poll succeeds on our change.
          { field: "acceptanceState", isAdditional: false, fromRaw: AcceptanceState.DECLINED },
        ],
      ],
    ]),
    });

      // Remove the entry under the MAIN-guest key (map is keyed by the guest
      // whose companions were fetched, never by the companion itself); scan
      // every list when the caller couldn't supply it.
    setAllCompanionsState((prev) => {
      const next = new Map(prev);
      const drop = (list: Guest[]) =>
        list.filter((c) => c.guestId !== companionId);
      const keyed = mainGuestId ? next.get(mainGuestId) : undefined;
      if (keyed) {
        next.set(mainGuestId!, drop(keyed));
        return next;
      }
      let touched = false;
      next.forEach((list, key) => {
        if (list?.some((c) => c.guestId === companionId)) {
          next.set(key, drop(list));
          touched = true;
        }
      });
      return touched ? next : prev;
    });
  }, [eventId, broadcastResolved]);

  /** Drop a companion from every host list it appears under. The map is keyed
   *  by HOST, so an unknown host means scanning — same shape the resolve paths
   *  above use, and the same historical bug they document (keying by the
   *  companion planted a junk entry that made `fetchCompanions` think it had
   *  a cache). */
  const dropFromMap = useCallback(
    (companionId: string, mainGuestId?: string) => {
      setAllCompanionsState((prev) => {
        const next = new Map(prev);
        const drop = (list: Guest[]) =>
          list.filter((c) => c.guestId !== companionId);
        const keyed = mainGuestId ? next.get(mainGuestId) : undefined;
        if (keyed) {
          next.set(mainGuestId!, drop(keyed));
          return next;
        }
        let touched = false;
        next.forEach((list, key) => {
          if (list?.some((c) => c.guestId === companionId)) {
            next.set(key, drop(list));
            touched = true;
          }
        });
        return touched ? next : prev;
      });
    },
    [],
  );

  const deleteCompanion = useCallback(
    async (companionId: string, mainGuestId?: string, options?: { permanent?: boolean }) => {
      // The write lives in GuestsProvider because deleting a guest is one
      // operation with one cascade (seats, sessions, tokens, sent mails, the
      // contingent slot), and a second implementation of it here is exactly
      // how the popup came to have its own idea of what "remove" means.
      if (!guests) {
        // Refuse loudly rather than silently declining, which is the bug this
        // whole change exists to remove.
        throw new Error("unknown");
      }
      // Deliberately NOT the provider-wide `loading` — and not the
      // provider-wide `errorMessage` either.
      //
      // The rule for all three row actions: those two belong to LOADING the
      // list; a row action reports through the row. Setting either one here
      // was driven and confirmed to leave `{dialog:false, row:false}` on
      // screen — the panel replaced its rows with a banner, which unmounted
      // the row and the very dialog that was supposed to show the reason.
      //
      // `CompanionsList` renders a spinner INSTEAD of its rows while that flag
      // is set — which unmounts the row, and with it the confirmation dialog
      // this delete was started from. On the error path that would take the
      // error message down with it. The row owns its own busy state.
      // No try/catch: a failed delete REJECTS, and `useCompanionEntry` renders
      // the reason inside the still-open confirmation dialog. Catching it here
      // to re-throw added nothing but a lint error.
      const result = options
        ? await guests.deleteGuests({ mode: "ids", ids: [companionId] }, options)
        : await guests.deleteGuests({ mode: "ids", ids: [companionId] });

      // Bookkeeping, identical to `broadcastResolved`'s — and it is NOT
      // redundant with what `deleteGuests` already does.
      //
      // Which provider the popup shares depends on how it was opened, and
      // both cases need this: the launcher (seating, slotting, requests rows)
      // mounts a `StandaloneGuestDetail` whose provider holds the host alone,
      // so its row removal reaches nothing; the guest-list page renders the
      // detail INLINE under the table's own provider, where the row really is
      // removed but the requests caches and the other snapshots are not.
      // Either way the surfaces behind the popup would keep the deleted
      // companion until something else happened to refetch.
      dropGuestFromRequestsCache(queryClient, eventId, companionId, "companions");
      void queryClient.invalidateQueries({
        queryKey: ["guestsRequests", eventId],
        refetchType: "none",
      });
      void queryClient.invalidateQueries({
        queryKey: ["guestlist", eventId],
        refetchType: "none",
      });
      void queryClient.invalidateQueries({
        queryKey: ["guestDetailGuest", eventId, companionId],
        refetchType: "none",
      });

      dropFromMap(companionId, mainGuestId);

      // The HOST moved too, and not only on screen: the delete recomputes
      // `numCompanions` and releases the counted slot server-side, so every
      // computed field over that number ("Gesamtanzahl = numCompanions + 1")
      // already holds a new value the popup has not read yet.
      //
      // An ACTIVE invalidation, unlike the three above: this is a synchronous
      // server-side recompute, not a workflow the browser has to wait for, so
      // there is nothing to poll for — the correct value is one refetch away
      // and the host's card is on screen right now. (`armGuestReconcile` is
      // for the other case: a value an automation will write later.)
      if (mainGuestId) {
        void queryClient.invalidateQueries({
          queryKey: ["guestDetailGuest", eventId, mainGuestId],
        });
      }
      return result;
    },
    [eventId, guests, queryClient, dropFromMap],
  );

  const restoreCompanion = useCallback(
    async (companionId: string, mainGuestId?: string) => {
      // "Removed" has TWO spellings on a companion row, and putting one back
      // needs the matching undo — reading the row rather than assuming, because
      // an archived companion that is merely re-accepted stays archived, i.e.
      // stays out of the party, while the button claims it brought them back.
      let archived = false;
      let declined = true;
      for (const list of allCompanionsState.values()) {
        const hit = list?.find((c) => c.guestId === companionId);
        if (!hit) continue;
        archived = hit.archived === true;
        declined = hit.acceptanceState === AcceptanceState.DECLINED;
        break;
      }

      if (archived) {
        if (!guests) throw new Error("unknown");
        await guests.unarchiveGuests({ mode: "ids", ids: [companionId] });
        // `unarchiveGuests` runs `removeGuestsAndRefresh`, which is written for
        // the guest list's ARCHIVED view: it drops the row and takes one off
        // `totalCount`. On the guest-list page the popup shares that very
        // provider (`GuestsCard` renders the detail inline), so on the ordinary
        // list it would decrement at the moment the guest becomes visible.
        // Re-read the page instead of trusting the optimistic removal.
        guests.refreshGuests?.();
      }
      if (declined) {
        // The same write that approves a request — one lane for "this person
        // is in" — but NOT announced as a resolved request: this row was never
        // pending, and the announcement decrements the open-requests counters.
        await acceptCompanion(companionId, mainGuestId, {
          announceResolvedRequest: false,
        });
      }

      // The row stays in the list and shows its new state. Dropping it locally
      // made "restore" do what the trash beside it does — and left the host's
      // companion count one short at the moment the party grew.
      if (mainGuestId) {
        await fetchCompanions(
          mainGuestId,
          true,
          loadedScopeRef.current.get(mainGuestId),
        );
        void queryClient.invalidateQueries({
          queryKey: ["guestDetailGuest", eventId, mainGuestId],
        });
      }
    },
    [acceptCompanion, allCompanionsState, eventId, fetchCompanions, guests, queryClient],
  );

  const contextValue = useMemo(() => ({
    eventId,
    allCompanions: allCompanionsState, loadErrors, peekCompanions, loading, errorMessage, setAllCompanionsState,
    fetchCompanions, acceptCompanion, declineCompanion, deleteCompanion, restoreCompanion,
  }), [eventId, allCompanionsState, loadErrors, peekCompanions, loading, errorMessage,
    fetchCompanions, acceptCompanion, declineCompanion, deleteCompanion, restoreCompanion]);

  return (
    <GuestCompanionsContext.Provider
      value={contextValue}
    >
      {children}
    </GuestCompanionsContext.Provider>
  );
};

/** The companions context WITHOUT the throw — see `useGuestsOptional`. */
export const useGuestCompanionsOptional = () => useContext(GuestCompanionsContext);

export const useGuestCompanions = () => {
  const context = useContext(GuestCompanionsContext);
  if (!context) {
    throw new Error(
      "useGuestCompanions must be used within a GuestCompanionsProvider"
    );
  }
  return context;
};
